// ============================================================================
// barks.js — live in-battle dialogue. Short "barks" toast in from the bottom-left
// (portrait + name + one line) driven by what's actually happening: a warlord
// takes the field and taunts, a bastion flips, a general drops low and calls for
// backup, Benjen bleeds and the Warmaster shouts him back, a warlord falls and
// the Host roars. Levels can also fire scripted lines via EV.BARK. Non-blocking,
// queued, self-timed — pure DOM over the canvas, only visible while `body.playing`.
// ============================================================================

import { EV } from '../core/events.js';
import { PORTRAIT, SPEAKER, NAME2SPEAKER } from './screens.js';
import { ST_DEAD } from '../core/constants.js';

const CSS = `
#barks{position:fixed;left:24px;bottom:104px;z-index:13;pointer-events:none;width:min(440px,44vw);}
#barks .bark{display:flex;gap:12px;align-items:stretch;background:linear-gradient(90deg,rgba(8,5,4,.9),rgba(8,5,4,.55));
  border:1px solid rgba(255,106,26,.34);border-left:3px solid #ff7a2a;padding:9px 12px;opacity:0;transform:translateX(-14px);
  transition:opacity .18s,transform .18s;box-shadow:0 6px 20px rgba(0,0,0,.5);}
#barks .bark.show{opacity:1;transform:none;}
#barks .bark.foe{border-left-color:#ff4a2a;} #barks .bark.ally{border-left-color:#7fa6d8;}
#barks .por{width:56px;height:56px;flex:0 0 auto;object-fit:cover;border:1px solid rgba(255,106,26,.4);background:#0a0706;align-self:center;}
#barks .por.none{display:none;}
#barks .tx{min-width:0;display:flex;flex-direction:column;justify-content:center;}
#barks .who{color:#ff8a3a;font:800 11px/1 'Trebuchet MS',sans-serif;letter-spacing:.16em;margin-bottom:5px;}
#barks .bark.ally .who{color:#9ab6e0;}
#barks .line{color:#ece3d6;font:15px/1.4 'Trebuchet MS',sans-serif;text-shadow:0 1px 3px #000;}
`;

// warlord taunts on entering the fight (keyed by name; generic pool otherwise)
const TAUNTS = {
  'Ashlord Kaevun': 'Nine hosts I have broken. Yours makes ten, Daguth.',
  'Vurmog': 'Come, little ember. Vurmog will snuff you out.',
  'Draxa Embermaw': 'The slag is hungry. I will feed it your Host.',
  'Tholvar': 'I swore this ground would not yield. Try me and die.',
  'Skarn Duraghast': 'Ruin walks. Stand still and be broken.',
  'Ysra Coalheart': 'I have made widows of better men than you.',
  'Jarl Hoarfrost': 'The cold takes everything. It will take you slow.',
  'Yrsa Palewind': 'My spear has drunk warmer blood than yours.',
  'Gravelord Murk': 'Rot to me, little commander. Rot to me.',
  'Sythe the Hollow': 'The bog remembers every fool who crossed it.',
  'Ourang-Vael': 'I was old when your black sword was ore. You cannot end me.',
};
const TAUNT_GENERIC = ['You die on this ground.', 'The Dynasty does not yield.', 'Burn, Ashen dog.', 'Your Host ends here.'];

const ALLY_LOW = [
  'Need backup, Commander — they\'re all over me!', 'I can\'t hold this line alone!',
  'To me, Benjen! To me!', 'They\'ve got me surrounded — help!', 'I\'m bleeding out here, Commander!',
];
const PLAYER_LOW = [
  'Commander, you\'re bleeding! Fall back and let us cover you!', 'Benjen — pull back! You\'re no good to us dead!',
  'Hold on, Commander! We\'re carving toward you!',
];
const WARLORD_DOWN = ['Their warlord is down! Press them!', 'One less banner! Forward, the Host!', 'The Dynasty bleeds! On, on!'];
const BASTION_WON = ['The bastion is ours! Raise the black and silver!', 'Another hold falls to us — push on!'];
const BASTION_LOST = ['They\'ve taken the bastion back — we have to move!', 'A hold is lost, Commander! Retake it!'];
const TITAN_ROAR = ['By the black sword — look at him go!', 'TITANFALL! Stay clear of the Commander!'];

const pick = (a) => a[(Math.random() * a.length) | 0];

export class Barks {
  constructor(hudRoot, world) {
    const style = document.createElement('style'); style.textContent = CSS; document.head.appendChild(style);
    const root = document.createElement('div'); root.id = 'barks';
    root.innerHTML = `<div class="bark"><img class="por" alt=""><div class="tx"><div class="who"></div><div class="line"></div></div></div>`;
    hudRoot.appendChild(root);
    this.el = root.querySelector('.bark');
    this.por = root.querySelector('.por');
    this.whoEl = root.querySelector('.who');
    this.lineEl = root.querySelector('.line');

    this.queue = [];
    this.cur = null; this.curT = 0; this.gap = 0;
    this._cool = {};            // cooldownKey -> seconds remaining
    this._playerLow = false;

    const ev = world.events;
    ev.on(EV.BARK, ({ who, line, name, tone }) => this._push({ who, line, name, side: tone === 'good' ? 'ally' : (who === 'MAGMAR' ? 'foe' : 'ally') }));
    ev.on(EV.WARLORD_ENGAGED, ({ warlord }) => {
      const nm = warlord.warlord ? warlord.warlord.name : 'Magmar Warlord';
      this._push({ who: 'MAGMAR', name: nm, line: TAUNTS[nm] || pick(TAUNT_GENERIC), side: 'foe' }, 'taunt:' + nm, 999);
    });
    ev.on(EV.WARLORD_SLAIN, ({ warlord }) => {
      if (warlord && warlord.warlord && warlord.warlord.ally) return; // ally death handled by ANNOUNCE
      this._push({ who: pickAllySpeaker(world), line: pick(WARLORD_DOWN), side: 'ally' }, 'wdown', 6);
    });
    ev.on(EV.BASTION_CAPTURED, ({ team }) => {
      const player = team === 0;
      this._push({ who: pickAllySpeaker(world), line: player ? pick(BASTION_WON) : pick(BASTION_LOST), side: 'ally' }, 'bastion', 5);
    });
    ev.on(EV.TITANFALL, () => this._push({ who: pickAllySpeaker(world), line: pick(TITAN_ROAR), side: 'ally' }, 'titan', 8));
    ev.on(EV.BATTLE_START, () => { this.queue.length = 0; this.cur = null; this.el.classList.remove('show'); this._cool = {}; this._playerLow = false; });
  }

  // enqueue a bark; cooldownKey throttles a repeating source. Returns true iff the
  // bark was actually queued (false when the cooldown rejected it) — callers use
  // this so a "latch" flag isn't set for a bark that never fired.
  _push(b, cooldownKey, cooldownSec) {
    if (cooldownKey) {
      if ((this._cool[cooldownKey] || 0) > 0) return false;
      this._cool[cooldownKey] = cooldownSec || 4;
    }
    if (this.queue.length > 3) this.queue.shift();
    this.queue.push(b);
    return true;
  }

  update(world, dt) {
    for (const k in this._cool) { this._cool[k] -= dt; if (this._cool[k] <= 0) delete this._cool[k]; }

    if (!world.gameOver) this._pollDanger(world);

    if (this.cur) {
      this.curT += dt;
      if (this.curT >= this.cur.dur) { this.cur = null; this.el.classList.remove('show'); this.gap = 0.32; }
    } else if (this.gap > 0) {
      this.gap -= dt;
    } else if (this.queue.length) {
      this._render(this.queue.shift());
    }
  }

  // watch for a general dropping low (call for backup) or Benjen bleeding out
  _pollDanger(world) {
    for (let i = 0; i < world.generals.length; i++) {
      const g = world.generals[i];
      if (!g._active || g.state === ST_DEAD) continue;
      const frac = g.hp / g.maxHp;
      if (frac < 0.32 && !g._barkLow) {
        // only latch once the cry-for-backup actually queues, so a bark the shared
        // cooldown rejected can still fire on a later tick (else it's lost for the episode)
        const who = (g.warlord && NAME2SPEAKER[g.warlord.name]) || 'HALDRIC';
        if (this._push({ who, name: g.warlord ? undefined : null, line: pick(ALLY_LOW), side: 'ally' }, 'alow', 9)) g._barkLow = true;
      } else if (frac > 0.55) g._barkLow = false;
    }
    const p = world.player;
    const pf = p.hp / p.maxHp;
    if (pf < 0.28 && p.state !== 'dead' && !this._playerLow) {
      if (this._push({ who: 'HALDRIC', line: pick(PLAYER_LOW), side: 'ally' }, 'plow', 10)) this._playerLow = true;
    } else if (pf > 0.5) this._playerLow = false;
  }

  _render(b) {
    this.cur = b; this.curT = 0; b.dur = b.dur || 4.4;
    this.el.classList.remove('foe', 'ally');
    if (b.side) this.el.classList.add(b.side);
    const url = PORTRAIT[b.who];
    if (url) { this.por.src = url; this.por.classList.remove('none'); } else this.por.classList.add('none');
    this.whoEl.textContent = b.name || SPEAKER[b.who] || b.who || '';
    this.lineEl.textContent = '"' + b.line + '"';
    // force reflow so the transition re-triggers, then show
    void this.el.offsetWidth;
    this.el.classList.add('show');
  }
}

// pick a living ally general's speaker key for a "the Host" line (fallback Haldric)
function pickAllySpeaker(world) {
  const alive = world.generals.filter((g) => g._active && g.state !== ST_DEAD && g.warlord && NAME2SPEAKER[g.warlord.name]);
  if (alive.length) return NAME2SPEAKER[pick(alive).warlord.name];
  return 'HALDRIC';
}
